/**
 * LOYALTY PROGRAM — the business side, inside the Command Center.
 *
 * ONE generic panel for every vertical: the label comes from the industry
 * profile, so no per-vertical Command Center code exists anywhere. Rendered by
 * the shell only when the organization's program is on or being set up, and
 * only for someone allowed to set it up.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Gift, Loader2, Users } from 'lucide-react';
import api from '../../../lib/api';
import { useAuth } from '../../../lib/auth';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Switch } from '../../../components/ui/switch';
import { Label } from '../../../components/ui/label';
import { DesignEditor } from '../../../components/loyalty/DesignEditor';
import { DeskPanel } from '../../../components/loyalty/DeskPanel';
import { amount as amountOnly, money, moneyFieldProps, plain } from '../../../lib/money';
import { MemberScreen } from '../../../components/loyalty/MemberScreen';
import { DeletedItems } from '../../../components/loyalty/DeletedItems';
import { downloadConsentCsv } from '../../../components/loyalty/consentCsv';
import { ActionButton } from '../../../components/loyalty/actionStyles';

const SWITCHES = ['visit_points', 'cash_in', 'points_card', 'contact', 'bookings', 'my_vip',
    'bank', 'create', 'history', 'referral', 'reward_email'];

const Stat = ({ label, value, testid }) => (
    <div className="rounded-xl border border-slate-200 bg-white p-4 text-center shadow-sm
                    dark:border-slate-700/50 dark:bg-slate-900/40 dark:shadow-none">
        <p className="text-2xl font-bold text-slate-900 dark:text-slate-100" data-testid={testid}>{value}</p>
        <p className="text-xs text-slate-500 dark:text-slate-400">{label}</p>
    </div>
);

export default function LoyaltyProgramPanel({ data, organizationId, startOpen = false }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { isSuperAdmin } = useAuth();
    const figures = data?.loyalty || {};
    const [cfg, setCfg] = useState(null);
    const [members, setMembers] = useState([]);
    const [consentBusy, setConsentBusy] = useState(false);
    const [open, setOpen] = useState(startOpen);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [resetting, setResetting] = useState('');
    const [resetNote, setResetNote] = useState('');
    const [search, setSearch] = useState('');
    const [sort, setSort] = useState('recent');
    const [pointValue, setPointValue] = useState('');
    const [cashMin, setCashMin] = useState('');
    const [cashMax, setCashMax] = useState('');
    const [demo, setDemo] = useState(null);          // null = not a demo clinic for me
    const [demoAsk, setDemoAsk] = useState('');      // 'load' | 'remove' | ''
    const [demoBusy, setDemoBusy] = useState(false);
    const [demoSummary, setDemoSummary] = useState(null);
    const [openMember, setOpenMember] = useState(null);   // {id, tab}
    const [designOpen, setDesignOpen] = useState(false);
    const [deskOpen, setDeskOpen] = useState(false);
    const [deskPending, setDeskPending] = useState(0);
    const [designScreen, setDesignScreen] = useState('');
    const [deletedOpen, setDeletedOpen] = useState(false);

    const load = useCallback(async () => {
        if (!organizationId) return;
        try {
            const r = await api.get('/loyalty/settings', { params: { organization_id: organizationId } });
            setCfg(r.data);
            setPointValue(r.data?.point_value ?? '');
            setCashMin(r.data?.cash_in_min == null ? '' : amountOnly(r.data.cash_in_min));
            setCashMax(r.data?.cash_in_max == null ? '' : amountOnly(r.data.cash_in_max));
            if (r.data?.switches?.desk) {
                api.get('/loyalty/desk/pending', { params: { organization_id: organizationId } })
                    .then((q) => setDeskPending(q.data?.count || 0))
                    .catch(() => setDeskPending(0));
            }
            const m = await api.get('/loyalty/members',
                { params: { organization_id: organizationId, search: search || undefined, sort } });
            setMembers(m.data?.members || []);
        } catch (e) {
            setError(e.response?.data?.detail || t('common.error', 'Something went wrong'));
        }
    }, [organizationId, search, sort, t]);

    useEffect(() => { load(); }, [load]);

    // Only a super admin on the one [DEMO] clinic gets an answer here, so the
    // block exists for nobody else — open or closed.
    const loadDemoPlan = useCallback(async () => {
        if (!organizationId) return;
        try {
            const d = await api.get('/loyalty/demo/plan',
                { params: { organization_id: organizationId } });
            setDemo(d.data);
        } catch { setDemo(null); }
    }, [organizationId]);
    useEffect(() => { loadDemoPlan(); }, [loadDemoPlan]);

    const sendReset = async (member) => {
        setResetting(member.id); setResetNote(''); setError('');
        try {
            const r = await api.post(`/loyalty/members/${member.id}/pin-reset`);
            setResetNote(t('loyalty.cc.resetSentTo', 'Reset email sent to {{email}}',
                { email: r.data?.masked_email || '' }));
        } catch (e) {
            setError(e.response?.data?.detail === 'too_many_emails'
                ? t('loyalty.cc.resetTooMany', 'Already sent a few times — try again later')
                : (e.response?.data?.detail || t('common.error', 'Something went wrong')));
        } finally { setResetting(''); }
    };


    /** First-time start: creates the settings record with the program OFF. */
    const startProgram = async () => {
        setBusy(true); setError('');
        try {
            await api.put('/loyalty/settings',
                { organization_id: organizationId, patch: { enabled: false } });
            await load();
        } catch (e) {
            setError(e.response?.data?.detail || t('common.error', 'Something went wrong'));
        } finally { setBusy(false); }
    };

    const runDemo = async (what) => {
        setDemoBusy(true); setError(''); setResetNote('');
        try {
            const r = await api.post(`/loyalty/demo/${what}`, null,
                { params: { organization_id: organizationId } });
            setDemoSummary(r.data?.summary || null);
            setDemoAsk('');
            await loadDemoPlan();
            if (open) await load();
        } catch (e) {
            let d = e.response?.data?.detail;
            if (typeof d === 'string' && d.trim().startsWith('{')) {
                try { d = JSON.parse(d); } catch { /* leave it as text */ }
            }
            const reason = (d && typeof d === 'object') ? d.reason : d;
            setError(t(`loyalty.cc.demo.${reason}`, reason || 'Something went wrong'));
        } finally { setDemoBusy(false); }
    };

    const save = async (patch) => {
        setBusy(true); setError('');
        try {
            const r = await api.put('/loyalty/settings', { organization_id: organizationId, patch });
            setCfg((c) => ({ ...c, ...r.data }));
        } catch (e) {
            setError(e.response?.data?.detail === 'point_value_required'
                ? t('loyalty.cc.pointValueRequired',
                    'Type the dollar value of a point before switching the program on')
                : (e.response?.data?.detail || t('common.error', 'Something went wrong')));
        } finally { setBusy(false); }
    };

    const title = figures.label_keys?.programButton
        ? t(figures.label_keys.programButton, t('loyalty.cc.title', 'Loyalty Program'))
        : t('loyalty.cc.title', 'Loyalty Program');

    return (
        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm
                            dark:border-slate-700/50 dark:bg-slate-900/40 dark:shadow-none"
                 data-testid="cc-band-loyalty">
            <div className="mb-4 flex items-center justify-between gap-3">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-slate-200">
                    <Gift className="h-4 w-4 text-fuchsia-500" />
                    {title}
                </h3>
                <Button variant="outline" size="sm" data-testid="cc-act-loyalty-program"
                        onClick={() => setOpen((v) => !v)}>
                    {open ? t('common.close', 'Close') : t('loyalty.cc.setUp', 'Set up')}
                </Button>
            </div>

            {figures.enabled && (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="cc-loyalty-figures">
                    <Stat label={t('loyalty.cc.members', 'Members')} value={figures.members ?? 0}
                          testid="cc-loyalty-members" />
                    <Stat label={t('loyalty.cc.confirmed', 'Confirmed')} value={figures.members_confirmed ?? 0}
                          testid="cc-loyalty-confirmed" />
                    <Stat label={t('loyalty.cc.pointsGiven', 'Points given')}
                          value={(figures.points_given ?? 0).toLocaleString()} testid="cc-loyalty-points-given" />
                    <Stat label={t('loyalty.cc.pointsOutstanding', 'Points outstanding')}
                          value={(figures.points_outstanding ?? 0).toLocaleString()}
                          testid="cc-loyalty-points-outstanding" />
                    <Stat label={t('loyalty.cc.cardsCreated', 'Cards created')}
                          value={figures.cards_created ?? 0} testid="cc-loyalty-cards-created" />
                    <Stat label={t('loyalty.cc.cardsGiven', 'Cards given')}
                          value={figures.cards_given ?? 0} testid="cc-loyalty-cards-given" />
                    <Stat label={t('loyalty.cc.cardsUsed', 'Cards used')}
                          value={figures.cards_used ?? 0} testid="cc-loyalty-cards-used" />
                    <Stat label={t('loyalty.cc.cardsUnused', 'Cards unused')}
                          value={figures.cards_unused ?? 0} testid="cc-loyalty-cards-unused" />
                    <Stat label={t('loyalty.cc.cardsValue', 'Total value')}
                          value={money(figures.cards_value ?? 0)}
                          testid="cc-loyalty-cards-value" />
                </div>
            )}

            {/* EVERY DAY-TO-DAY ACTION, ONE TAP FROM HERE. Nothing a manager
                uses daily sits inside the settings below. */}
            <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="cc-loyalty-quick">
                <Button size="sm" variant="outline" data-testid="cc-loyalty-quick-design"
                        onClick={() => setDesignOpen(true)}>
                    {t('loyalty.cc.designPortal', 'Design the portal')}
                </Button>
                <Button size="sm" variant="outline" data-testid="cc-loyalty-quick-desk"
                        onClick={() => setDeskOpen(true)}>
                    {t('loyalty.desk.panelTitle', 'Org Loyalty Portal')}
                    {deskPending > 0 && (
                        <span className="ml-2 rounded-full bg-rose-600 px-2 py-0.5 text-[10px] text-white"
                              data-testid="cc-loyalty-desk-badge">{deskPending}</span>
                    )}
                </Button>
                <Button size="sm" variant="outline" data-testid="cc-loyalty-quick-settings"
                        onClick={() => setOpen((v) => !v)}>
                    {t('loyalty.cc.programSettings', 'Program settings')}
                </Button>
                {isSuperAdmin() && (
                    <Button size="sm" variant="outline" data-testid="cc-loyalty-quick-deleted"
                            onClick={() => setDeletedOpen(true)}>
                        {t('loyalty.deleted.title', 'Deleted items')}
                    </Button>
                )}
                {cfg && cfg.has_record !== false && (
                    <span className="ml-auto inline-flex items-center gap-2 rounded-lg border
                                     border-fuchsia-200 px-3 py-1.5 dark:border-fuchsia-800">
                        <span className="text-xs font-semibold">
                            {t('loyalty.cc.master', 'Program is live')}
                        </span>
                        <Switch checked={!!cfg.enabled} disabled={busy}
                                data-testid="cc-loyalty-quick-master"
                                onCheckedChange={(v) => save({ enabled: v })} />
                    </span>
                )}
            </div>

            {demo && (
                <div className="mt-4 rounded-xl border border-dashed border-slate-300 p-3                                dark:border-slate-700" data-testid="cc-loyalty-demo">
                    <p className="text-xs font-semibold">
                        {t('loyalty.cc.demo.title', 'Demo members')}
                    </p>
                    <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
                        {t('loyalty.cc.demo.line',
                            'For the showroom only, on this [DEMO] clinic. Nothing is emailed and no program setting is changed.')}
                    </p>
                    {demo.missing.includes('loyalty_card') ? (
                        <div className="mt-2 space-y-2" data-testid="cc-loyalty-demo-blocked">
                            <p className="text-xs text-red-500">
                                {t('loyalty.cc.demo.no_loyalty_card_long',
                                    'Set one card of this clinic to the Loyalty portal in Card settings first')}
                            </p>
                            <Button size="sm" variant="outline" data-testid="cc-loyalty-demo-cards-btn"
                                    onClick={() => navigate('/cards')}>
                                {t('loyalty.cc.demo.cardSettings', 'Card settings')}
                            </Button>
                        </div>
                    ) : (
                        <div className="mt-2 flex flex-wrap gap-2">
                            {demo.already_seeded > 0 ? (
                                <Button size="sm" variant="outline" disabled={demoBusy}
                                        data-testid="cc-loyalty-demo-remove-btn"
                                        onClick={() => { setDemoSummary(null); setDemoAsk('remove'); }}>
                                    {t('loyalty.cc.demo.remove', 'Remove demo members')}
                                </Button>
                            ) : (
                                <Button size="sm" variant="outline" disabled={demoBusy}
                                        data-testid="cc-loyalty-demo-load-btn"
                                        onClick={() => { setDemoSummary(null); setDemoAsk('load'); }}>
                                    {t('loyalty.cc.demo.load', 'Load demo members')}
                                </Button>
                            )}
                        </div>
                    )}

                    {demoAsk === 'load' && (
                        <div className="mt-3 space-y-1 text-[11px]" data-testid="cc-loyalty-demo-confirm-load">
                            <p className="font-semibold">
                                {t('loyalty.cc.demo.willUse', 'These saved rules will be used:')}
                            </p>
                            <p>{t('loyalty.cc.pointValue', 'Dollar value of one point')}: ${demo.rules.point_value ?? '—'}</p>
                            <p>{t('loyalty.cc.openingPoints', 'Opening gift')}: {demo.rules.opening_points ?? '—'}</p>
                            <p>{t('loyalty.cc.pointsToCashIn', 'Points to cash in')}: {demo.rules.points_to_cash_in ?? '—'}</p>
                            <p>{t('loyalty.cc.rewardMode', 'What the program gives')}: {demo.rules.reward_mode}</p>
                            <p>{t('loyalty.cc.demo.visitRule', 'Visit rule')}: {demo.rules.visit_points_mode}
                                {' '}{demo.rules.visit_points_mode === 'fixed'
                                    ? demo.rules.visit_points_fixed
                                    : `${demo.rules.visit_points_percent}%`}
                                {' · '}{demo.rules.visit_min_hours} h
                                {' · '}{t('loyalty.cc.visitPointsMax', 'Maximum points per visit')}:{' '}
                                {demo.rules.visit_points_max ?? t('loyalty.cc.noLimit', 'No limit')}</p>
                            <p>{t('loyalty.cc.demo.switchesOn', 'Switches on')}: {demo.rules.switches_on.join(', ') || '—'}</p>
                            <p>{t('loyalty.cc.demo.card', 'Loyalty card')}: {demo.card_id}</p>
                            <p>{t('loyalty.cc.demo.willCreate', '{{count}} members will be created',
                                { count: demo.members_to_create })}</p>
                            {demo.missing.length > 0 && (
                                <p className="text-red-500" data-testid="cc-loyalty-demo-missing">
                                    {t('loyalty.cc.demo.settings_incomplete',
                                        'Set a dollar value for one point first')} ({demo.missing.join(', ')})
                                </p>
                            )}
                            <div className="flex gap-2 pt-1">
                                <Button size="sm" disabled={demoBusy || demo.missing.length > 0}
                                        data-testid="cc-loyalty-demo-load-go"
                                        onClick={() => runDemo('load')}>
                                    {demoBusy ? <Loader2 className="h-4 w-4 animate-spin" />
                                        : t('common.confirm', 'Confirm')}
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => setDemoAsk('')}
                                        data-testid="cc-loyalty-demo-cancel">
                                    {t('common.cancel', 'Cancel')}
                                </Button>
                            </div>
                        </div>
                    )}

                    {demoAsk === 'remove' && (
                        <div className="mt-3 space-y-1 text-[11px]" data-testid="cc-loyalty-demo-confirm-remove">
                            <p data-testid="cc-loyalty-demo-count">
                                {t('loyalty.cc.demo.willRemove',
                                    '{{count}} demo members will be removed. Members who registered themselves stay.',
                                    { count: demo.already_seeded })}
                            </p>
                            <div className="flex gap-2 pt-1">
                                <Button size="sm" disabled={demoBusy}
                                        data-testid="cc-loyalty-demo-remove-go"
                                        onClick={() => runDemo('remove')}>
                                    {demoBusy ? <Loader2 className="h-4 w-4 animate-spin" />
                                        : t('common.confirm', 'Confirm')}
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => setDemoAsk('')}
                                        data-testid="cc-loyalty-demo-cancel">
                                    {t('common.cancel', 'Cancel')}
                                </Button>
                            </div>
                        </div>
                    )}

                    {demoSummary && (
                        <p className="mt-3 text-[11px] text-emerald-600 dark:text-emerald-400"
                           data-testid="cc-loyalty-demo-summary">
                            {t('loyalty.cc.demo.summary',
                                '{{members}} members · {{points}} points outstanding · {{visits}} visits · cards: {{created}} created, {{given}} given, {{used}} used, {{unused}} unused · ${{value}}',
                                { members: demoSummary.members, points: demoSummary.points_outstanding,
                                  visits: demoSummary.visits, created: demoSummary.cards_created,
                                  given: demoSummary.cards_given, used: demoSummary.cards_used,
                                  unused: demoSummary.cards_unused, value: demoSummary.cards_value })}
                        </p>
                    )}
                </div>
            )}

            {/* THE MEMBERS, ALWAYS ON SCREEN — not inside the settings. */}
            <div className="mt-4" data-testid="cc-loyalty-members-block">
                <h4 className="mb-2 flex items-center gap-2 text-sm font-semibold">
                    <Users className="h-4 w-4 text-slate-400" />
                    {t('loyalty.cc.membersList', 'Members')} ({members.length})
                </h4>
                <div className="flex flex-wrap items-center gap-2 pb-2">
                    <input value={search} onChange={(e) => setSearch(e.target.value)}
                           placeholder={t('loyalty.cc.searchMembers', 'Search by name or email')}
                           data-testid="cc-loyalty-member-search"
                           className="w-56 rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm
                                      dark:border-slate-700 dark:bg-slate-900" />
                    <select value={sort} onChange={(e) => setSort(e.target.value)}
                            data-testid="cc-loyalty-member-sort"
                            className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm
                                       dark:border-slate-700 dark:bg-slate-900">
                        <option value="recent">{t('loyalty.cc.sortRecent', 'Newest first')}</option>
                        <option value="name">{t('loyalty.cc.sortName', 'By name')}</option>
                        <option value="points">{t('loyalty.cc.sortPoints', 'Most points')}</option>
                    </select>
                </div>
                <div className="flex flex-wrap items-center gap-2 pb-3"
                     data-testid="cc-loyalty-consent-block">
                    <Button size="sm" variant="outline" disabled={consentBusy}
                            data-testid="cc-loyalty-consent-csv"
                            onClick={async () => {
                                setConsentBusy(true);
                                try { await downloadConsentCsv(organizationId); }
                                finally { setConsentBusy(false); }
                            }}>
                        {consentBusy ? <Loader2 className="h-4 w-4 animate-spin" />
                            : t('loyalty.consent.download', 'Download consent records (CSV)')}
                    </Button>
                    <span className="text-[11px] text-slate-500 dark:text-slate-400"
                          data-testid="cc-loyalty-consent-warning">
                        {t('loyalty.consent.warning',
                            'This file contains personal information. Handle it like your client list.')}
                    </span>
                </div>
                {members.length === 0 ? (
                    <p className="text-sm text-slate-500" data-testid="cc-loyalty-members-empty">
                        {t('loyalty.cc.membersEmpty', 'Nobody has registered yet.')}
                    </p>
                ) : (
                    <ul className="space-y-2" data-testid="cc-loyalty-members-table">
                        {members.map((m) => (
                            <li key={m.id} className="rounded-xl border border-slate-200 p-3
                                                      dark:border-slate-700/50">
                                <div className="flex flex-wrap items-baseline justify-between gap-2">
                                    <button type="button" className="text-sm font-semibold underline"
                                            data-testid={`cc-loyalty-open-${m.id}`}
                                            onClick={() => setOpenMember({ id: m.id, tab: 'actions' })}>
                                        {m.first_name}
                                    </button>
                                    <span className="text-xs text-slate-500">{m.email}</span>
                                    <span className="text-xs">
                                        <span data-testid={`cc-loyalty-points-${m.id}`}>
                                            {(m.points || 0).toLocaleString()}
                                        </span>{' '}{t('loyalty.cc.points', 'Points')}
                                        {' · '}
                                        <span data-testid={`cc-loyalty-bank-count-${m.id}`}>
                                            {m.cards_in_bank}
                                        </span>{' '}{t('loyalty.cc.cardsInBank', 'Cards in bank')}
                                    </span>
                                </div>
                                <div className="mt-2 flex flex-wrap gap-1.5">
                                    <ActionButton action="points"
                                                  testid={`cc-loyalty-adjust-${m.id}`}
                                                  label={t('loyalty.cc.adjustPoints', 'Add / remove points')}
                                                  onClick={() => setOpenMember({ id: m.id, tab: 'actions' })} />
                                    <ActionButton action="cards"
                                                  testid={`cc-loyalty-add-cards-${m.id}`}
                                                  label={t('loyalty.cc.addCards', 'Add gift cards')}
                                                  onClick={() => setOpenMember({ id: m.id, tab: 'actions' })} />
                                    {m.confirmed && (
                                        <ActionButton action="visit"
                                                      testid={`cc-loyalty-visit-${m.id}`}
                                                      label={t('loyalty.cc.recordVisit', 'Record a visit')}
                                                      onClick={() => setOpenMember({ id: m.id, tab: 'actions' })} />
                                    )}
                                    {m.confirmed && (
                                        <ActionButton action="pin" disabled={resetting === m.id}
                                                      testid={`cc-loyalty-reset-${m.id}`}
                                                      label={t('loyalty.cc.sendPinReset', 'Send PIN reset email')}
                                                      onClick={() => sendReset(m)} />
                                    )}
                                    <ActionButton action="history"
                                                  testid={`cc-loyalty-history-${m.id}`}
                                                  label={t('loyalty.member.history', 'History')}
                                                  onClick={() => setOpenMember({ id: m.id, tab: 'history' })} />
                                </div>
                            </li>
                        ))}
                        {resetNote && (
                            <p className="p-2 text-xs text-emerald-600 dark:text-emerald-400"
                               data-testid="cc-loyalty-reset-note">{resetNote}</p>
                        )}
                    </ul>
                )}
            </div>

            {openMember && (
                <MemberScreen memberId={openMember.id} startTab={openMember.tab}
                              onClose={() => setOpenMember(null)}
                              onChanged={load} />
            )}

            {deletedOpen && (
                <DeletedItems organizationId={organizationId}
                              onClose={() => setDeletedOpen(false)} onChanged={load} />
            )}

            {deskOpen && cfg && (
                <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/60 p-3 sm:p-6"
                     data-testid="cc-loyalty-desk-screen">
                    <div className="mx-auto max-w-4xl rounded-2xl bg-white p-4 shadow-xl dark:bg-slate-900">
                        <div className="mb-3 flex items-center justify-between gap-3">
                            <h3 className="text-lg font-bold">
                                {t('loyalty.desk.panelTitle', 'Org Loyalty Portal')}
                            </h3>
                            <Button size="sm" variant="ghost" data-testid="cc-loyalty-desk-close"
                                    onClick={() => setDeskOpen(false)}>
                                {t('common.close', 'Close')}
                            </Button>
                        </div>
                        <DeskPanel orgId={organizationId} cfg={cfg} busy={busy} onSave={save}
                                   onDesign={(screen) => { setDeskOpen(false);
                                       setDesignScreen(screen); setDesignOpen(true); }} />
                    </div>
                </div>
            )}

            {designOpen && cfg && (
                <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/60 p-3 sm:p-6"
                     data-testid="cc-loyalty-design-screen">
                    <div className="mx-auto max-w-6xl rounded-2xl bg-white p-4 shadow-xl dark:bg-slate-900">
                        <div className="mb-3 flex items-center justify-between gap-3">
                            <h3 className="text-lg font-bold">
                                {t('loyalty.cc.designPortal', 'Design the portal')}
                            </h3>
                            <Button size="sm" variant="ghost" data-testid="cc-loyalty-design-close"
                                    onClick={() => setDesignOpen(false)}>
                                {t('common.close', 'Close')}
                            </Button>
                        </div>
                        <DesignEditor orgId={organizationId} cfg={cfg} busy={busy} onSave={save}
                                      startScreen={designScreen} />
                    </div>
                </div>
            )}


            {open && !cfg && <Loader2 className="mx-auto mt-4 h-5 w-5 animate-spin" />}

            {open && cfg && cfg.has_record === false && (
                <div className="mt-5 space-y-3" data-testid="cc-loyalty-setup-start">
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                        {t('loyalty.cc.setUpLine',
                            'Nothing is live yet — this creates the settings with the program switched off, '
                            + 'so you can set it up before anyone sees it.')}
                    </p>
                    <Button size="sm" disabled={busy} data-testid="cc-loyalty-setup-btn"
                            onClick={startProgram}>
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" />
                            : t('loyalty.cc.setUpProgram', 'Set up VIP Rewards')}
                    </Button>
                    {error && <p className="text-xs text-red-500" data-testid="cc-loyalty-error">{error}</p>}
                </div>
            )}

            {open && cfg && cfg.has_record !== false && (
                <div className="mt-5 space-y-5" data-testid="cc-loyalty-settings">
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                        {t('loyalty.cc.guide',
                            'Three steps: name the program, type what one point is worth, then switch it on.')}
                    </p>

                    <div className="grid gap-3 sm:grid-cols-3">
                        <div>
                            <Label className="mb-1 block text-xs">{t('loyalty.cc.programName', 'Program name')}</Label>
                            <Input defaultValue={cfg.program_name || ''} data-testid="cc-loyalty-name"
                                   onBlur={(e) => save({ program_name: e.target.value })} />
                        </div>
                        <div>
                            <Label className="mb-1 block text-xs">{t('loyalty.cc.pointValue', 'Dollar value of one point')}</Label>
                            <Input type="number" step="0.0001" value={pointValue}
                                   placeholder="0.01" data-testid="cc-loyalty-point-value"
                                   onChange={(e) => setPointValue(e.target.value)}
                                   onBlur={(e) => save({ point_value: e.target.value })} />
                            {Number(pointValue) > 0 && (
                                <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400"
                                   data-testid="cc-loyalty-point-example">
                                    {t('loyalty.cc.pointExample',
                                        '1 point = ${{one}}, so 2,000 points = ${{many}}',
                                        { one: Number(pointValue).toFixed(2),
                                          many: amountOnly(Number(pointValue) * 2000) })}
                                </p>
                            )}
                        </div>
                        <div>
                            <Label className="mb-1 block text-xs">{t('loyalty.cc.openingPoints', 'Opening gift points')}</Label>
                            <Input type="number" defaultValue={cfg.opening_points ?? ''}
                                   data-testid="cc-loyalty-opening-points"
                                   onBlur={(e) => save({ opening_points: e.target.value })} />
                        </div>
                        <div>
                            <Label className="mb-1 block text-xs">{t('loyalty.cc.cashIn', 'Points needed to cash in')}</Label>
                            <Input type="number" defaultValue={cfg.points_to_cash_in ?? ''}
                                   data-testid="cc-loyalty-cash-in"
                                   onBlur={(e) => save({ points_to_cash_in: e.target.value })} />
                        </div>
                        <div>
                            <Label className="mb-1 block text-xs">
                                {t('loyalty.cc.cashInMin', 'Smallest redemption ($)')}
                            </Label>
                            <Input data-testid="cc-loyalty-cash-in-min"
                                   {...moneyFieldProps(cashMin, setCashMin)}
                                   onBlur={() => { setCashMin(cashMin ? amountOnly(plain(cashMin)) : '');
                                       save({ cash_in_min: plain(cashMin) }); }} />
                        </div>
                        <div>
                            <Label className="mb-1 block text-xs">
                                {t('loyalty.cc.cashInMax', 'Largest redemption ($)')}
                            </Label>
                            <Input data-testid="cc-loyalty-cash-in-max"
                                   {...moneyFieldProps(cashMax, setCashMax)}
                                   onBlur={() => { setCashMax(cashMax ? amountOnly(plain(cashMax)) : '');
                                       save({ cash_in_max: plain(cashMax) }); }} />
                            <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400"
                               data-testid="cc-loyalty-cash-in-note">
                                {t('loyalty.cc.cashInLimitsNote',
                                    'Leave both empty and a member may redeem any amount up to what she has.')}
                            </p>
                        </div>
                        <div className="sm:col-span-2">
                            <Label className="mb-1 block text-xs">{t('loyalty.cc.profile', 'Industry profile')}</Label>
                            <Input defaultValue={cfg.profile_id} disabled={!cfg.may_set_profile}
                                   data-testid="cc-loyalty-profile"
                                   onBlur={(e) => save({ profile_id: e.target.value })} />
                        </div>
                    </div>

                    {!cfg.has_contact_email && (
                        <p className="text-xs text-amber-600 dark:text-amber-400" data-testid="cc-loyalty-no-contact">
                            {t('loyalty.cc.noContactEmail',
                                'This organization has no contact email, so the Contact button cannot send one.')}
                        </p>
                    )}

                    <div className="space-y-3 rounded-lg border border-slate-200 p-3 dark:border-slate-700/50"
                         data-testid="cc-loyalty-earning">
                        <p className="text-xs font-semibold">{t('loyalty.cc.earning', 'How members earn')}</p>
                        <div>
                            <Label className="mb-1 block text-xs">
                                {t('loyalty.cc.rewardMode', 'What the program gives')}
                            </Label>
                            <select defaultValue={cfg.reward_mode || 'both'}
                                    data-testid="cc-loyalty-reward-mode"
                                    onChange={(e) => save({ reward_mode: e.target.value })}
                                    className="w-full rounded-lg border border-slate-200 px-2.5 py-2 text-sm
                                               sm:w-72 dark:border-slate-700 dark:bg-slate-900">
                                <option value="points">{t('loyalty.cc.modePointsOnly', 'Points only')}</option>
                                <option value="cards">{t('loyalty.cc.modeCardsOnly', 'Gift cards only')}</option>
                                <option value="both">{t('loyalty.cc.modeBoth', 'Both')}</option>
                            </select>
                            <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
                                {t('loyalty.cc.rewardModeNote',
                                    'Points only hides the Gift cards button; Gift cards only hides the points figures.')}
                            </p>
                        </div>
                        <p className="text-[11px] text-slate-500 dark:text-slate-400">
                            {t('loyalty.cc.visitNote',
                                'YulOnTap has no till or sales data: the amount for a visit is typed by your staff.')}
                        </p>
                        <div className="grid gap-3 sm:grid-cols-3">
                            <div>
                                <Label className="mb-1 block text-xs">
                                    {t('loyalty.cc.referralRewardType', 'Referral reward')}
                                </Label>
                                <select defaultValue={cfg.referral_reward_type || 'card'}
                                        data-testid="cc-loyalty-referral-type"
                                        onChange={(e) => save({ referral_reward_type: e.target.value })}
                                        className="w-full rounded-lg border border-slate-200 px-2.5 py-2 text-sm
                                                   dark:border-slate-700 dark:bg-slate-900">
                                    <option value="card">{t('loyalty.cc.rewardCard', 'Gift card')}</option>
                                    <option value="points">{t('loyalty.cc.rewardPoints', 'Points')}</option>
                                    <option value="both">{t('loyalty.cc.rewardBoth', 'Both')}</option>
                                </select>
                            </div>
                            <div>
                                <Label className="mb-1 block text-xs">
                                    {t('loyalty.cc.referralPointsMode', 'Referral points are')}
                                </Label>
                                <select defaultValue={cfg.referral_points_mode || 'percent'}
                                        data-testid="cc-loyalty-referral-mode"
                                        onChange={(e) => save({ referral_points_mode: e.target.value })}
                                        className="w-full rounded-lg border border-slate-200 px-2.5 py-2 text-sm
                                                   dark:border-slate-700 dark:bg-slate-900">
                                    <option value="percent">{t('loyalty.cc.modePercent', 'A percent of the card')}</option>
                                    <option value="fixed">{t('loyalty.cc.modeFixed', 'A fixed number')}</option>
                                </select>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <Label className="mb-1 block text-xs">{t('loyalty.cc.percent', 'Percent')}</Label>
                                    <Input type="number" step="0.1" defaultValue={cfg.referral_points_percent ?? ''}
                                           data-testid="cc-loyalty-referral-percent"
                                           onBlur={(e) => save({ referral_points_percent: e.target.value })} />
                                </div>
                                <div>
                                    <Label className="mb-1 block text-xs">{t('loyalty.cc.fixed', 'Fixed')}</Label>
                                    <Input type="number" defaultValue={cfg.referral_points_fixed ?? ''}
                                           data-testid="cc-loyalty-referral-fixed"
                                           onBlur={(e) => save({ referral_points_fixed: e.target.value })} />
                                </div>
                            </div>
                        </div>
                        <div className="grid gap-3 sm:grid-cols-3">
                            <div>
                                <Label className="mb-1 block text-xs">
                                    {t('loyalty.cc.visitPointsMode', 'Points per visit are')}
                                </Label>
                                <select defaultValue={cfg.visit_points_mode || 'percent'}
                                        data-testid="cc-loyalty-visit-mode"
                                        onChange={(e) => save({ visit_points_mode: e.target.value })}
                                        className="w-full rounded-lg border border-slate-200 px-2.5 py-2 text-sm
                                                   dark:border-slate-700 dark:bg-slate-900">
                                    <option value="percent">{t('loyalty.cc.modePercentSpend', 'A percent of the amount')}</option>
                                    <option value="fixed">{t('loyalty.cc.modeFixed', 'A fixed number')}</option>
                                </select>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <Label className="mb-1 block text-xs">{t('loyalty.cc.percent', 'Percent')}</Label>
                                    <Input type="number" step="0.1" defaultValue={cfg.visit_points_percent ?? ''}
                                           data-testid="cc-loyalty-visit-percent"
                                           onBlur={(e) => save({ visit_points_percent: e.target.value })} />
                                </div>
                                <div>
                                    <Label className="mb-1 block text-xs">{t('loyalty.cc.fixed', 'Fixed')}</Label>
                                    <Input type="number" defaultValue={cfg.visit_points_fixed ?? ''}
                                           data-testid="cc-loyalty-visit-fixed"
                                           onBlur={(e) => save({ visit_points_fixed: e.target.value })} />
                                </div>
                            </div>
                            <div>
                                <Label className="mb-1 block text-xs">
                                    {t('loyalty.cc.visitMinHours', 'Hours between visit credits')}
                                </Label>
                                <Input type="number" defaultValue={cfg.visit_min_hours ?? 12}
                                       data-testid="cc-loyalty-visit-hours"
                                       onBlur={(e) => save({ visit_min_hours: e.target.value })} />
                            </div>
                            <div>
                                <Label className="mb-1 block text-xs">
                                    {t('loyalty.cc.visitPointsMax', 'Maximum points per visit')}
                                </Label>
                                <Input type="number" defaultValue={cfg.visit_points_max ?? ''}
                                       placeholder={t('loyalty.cc.noLimit', 'No limit')}
                                       data-testid="cc-loyalty-visit-max"
                                       onBlur={(e) => save({ visit_points_max: e.target.value })} />
                            </div>
                        </div>
                    </div>

                    <div className="grid gap-2 sm:grid-cols-2">
                        {SWITCHES.map((key) => (
                            <div key={key} className="flex items-center justify-between gap-3 rounded-lg border
                                                      border-slate-200 px-3 py-2 dark:border-slate-700/50">
                                <span className="text-sm">{t(`loyalty.cc.switch.${key}`, key)}</span>
                                <Switch checked={!!cfg.switches?.[key]} disabled={busy}
                                        data-testid={`cc-loyalty-switch-${key}`}
                                        onCheckedChange={(v) => save({ [key]: v })} />
                            </div>
                        ))}
                    </div>

                    <div className="flex items-center justify-between gap-3 rounded-lg border border-fuchsia-200
                                    px-3 py-3 dark:border-fuchsia-800">
                        <span className="text-sm font-semibold">{t('loyalty.cc.master', 'Program is live')}</span>
                        <Switch checked={!!cfg.enabled} disabled={busy} data-testid="cc-loyalty-master"
                                onCheckedChange={(v) => save({ enabled: v })} />
                    </div>

                    {error && <p className="text-sm text-rose-500" data-testid="cc-loyalty-error">{error}</p>}

                </div>
            )}
        </section>
    );
}
